/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Camera, X, Info, Lock, HelpCircle, RotateCcw } from 'lucide-react';
import ReferenceTooltip from './capture/ReferenceTooltip';
import CaptureHeader from './CaptureHeader';
import NotesButton from './capture/NotesButton';
import GlobalInstructionButton from './capture/GlobalInstructionButton';
import './capture/capture.css';
import { captureProvenanceMetadata } from '../utils/provenanceService';
import { normalizeDisplayPhotos, requestLocation } from '../utils/captureUtils';
import captureClient from '../api/captureClient';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import captureSyncQueue, { isNetworkError } from '../utils/captureSyncQueue';
import useCachedAssetSrc from '../hooks/useCachedAssetSrc';
import CachedImage from './CachedImage';
import cameraWhiteIcon from '../assets/camera-white.svg';
import scannerWhiteIcon from '../assets/scanner-white.svg';
import videoWhiteIcon from '../assets/video-white.svg';
import filesIconAsset from '../assets/files.svg';
import { getIdentifierLabel } from '../constants/workflowOptions';
import { MIME_MAP, getMediaCategory } from '../constants/mimeMap';
import { validateIdentifier } from '../utils/identifierValidation';
import fileAttachedPin from '../assets/file-attached-pin.svg';
import { uploadFile as multipartUploadFile, shouldUseMultipartUpload, MAX_FILE_SIZE_BYTES } from '../utils/multipartUpload';
import { getNoteIdentity } from '../utils/noteIdentityService';

const parseUploadError = (error) => {
  // A local-storage failure (offlineDb.saveMedia hitting the device/browser's
  // IndexedDB quota) is a completely different problem from connectivity —
  // must be checked first, since it can just as easily happen while offline
  // as while online, and `!navigator.onLine` alone can't tell them apart.
  if (error?.name === 'QuotaExceededError') {
    return "This device is out of local storage space, so the file couldn't be saved for upload. Free up some space (or clear other queued items) and try again.";
  }
  if(error?.name === 'BlobStorageUnsupportedError') {
    return error.message;
  }
  if (!navigator.onLine) return 'No internet connection. Please check and try again.';
  const msg = error.response?.data?.message || error.message || '';
  if (msg.includes('File buffer is empty') || msg.includes('size: 0'))
    return 'The file appears to be empty or could not be read. Please try again with a valid file.';
  if (msg.includes('File extension mismatch') || msg.includes('does not match its type') || msg.includes('Content type mismatch'))
    return 'The file format does not match its extension. Please check the file and try again.';
  if (msg.includes('Upload processing failed'))
    return 'The file was rejected during processing. Please check the file format and try again.';
  if (msg.includes('exceeds the maximum'))
    return msg;
  return msg || 'Upload failed. Please try again.';
};

// Named constants 
const FLASH_DURATION_MS = 150;
const IMAGE_QUALITY = 0.9;
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
const MIME_IMAGE_JPEG = 'image/jpeg';
const MIME_VIDEO_MP4 = 'video/mp4';

const ERR_CAMERA_PERMISSION = 'Camera access is required. Please allow access in your browser settings.';
const FullScreenViewer = ({ url, type, onClose, duration, token }) => {
  const [videoLoading, setVideoLoading] = useState(true);
  const { src: mediaSrc, tryFallback: tryMediaFallback } = useCachedAssetSrc(token, url);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 20, right: 20, zIndex: 10000, background: 'rgba(255, 255, 255, 0.79)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black' }}
      >
        <X size={20} />
      </button>
      {type === 'video' ? (
        <>
          {videoLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
              <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}
          <video
            src={mediaSrc}
            controls
            autoPlay
            playsInline
            onClick={e => e.stopPropagation()}
            onError={tryMediaFallback}
            style={{ width: '100vw', height: '100vh', objectFit: 'contain', opacity: 0, transition: 'opacity 0.2s' }}
            onLoadedData={(e) => {
              const seekTo = parseInt(duration, 10);
              if (Number.isFinite(seekTo)) {
                e.target.currentTime = seekTo;
              }
            }}
            onCanPlayThrough={(e) => { e.target.style.opacity = 1; setVideoLoading(false); }}
          />
        </>
      ) : (
        <img
          src={mediaSrc}
          alt="Full view"
          onClick={e => e.stopPropagation()}
          onError={tryMediaFallback}
          style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
        />
      )}
    </div>
  );
};

FullScreenViewer.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  onClose: PropTypes.func,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
};

const runSaveAndExit = async (handleSubmit, onSaveAndExit) => {
  const uploaded = await handleSubmit();
  if (uploaded) {
    onSaveAndExit();
  }
};

/**
 * FileUploadPane — inline sub-component for item.type === 'file'
 * Renders a native file picker restricted by MIME type, validates the
 * selection client-side, and submits via the same upload endpoint used
 * by photo/video steps.
 */
const FileUploadPane = ({ item, token, assignment, workflow, location, onCapture, branding, stepNumber, totalSteps, onBack, onNext, uploading, setUploading, uploadProgress, setUploadProgress, nameTooltipTriggerRef, showNameTooltip, setShowNameTooltip, showTooltip, setShowTooltip, tooltipTriggerRef, instructionRef, canExpand, setIsExpanded, isExpanded, captured, replaceUploadContext, offlineSupportEnabled = true, onOpenNotes, onSaveAndExit }) => {
  // Caching (not uploads) is skipped entirely when offline support is off — see assetCache.js.
  const cacheToken = offlineSupportEnabled ? token : null;
  const [selectedFile, setSelectedFile] = useState(captured?.file || null);
  const uploadedFileName = !captured?.file && captured?.item?.media?.fileName ? captured.item.media.fileName : null;
  const [replacing, setReplacing] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const currentTheme = branding?.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'aqua', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);
  const fileRef = useRef(null);
  const pickerReadyRef = useRef(false);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // allowedFileType is no longer configured per-step — accept all 21 supported types
  // const allowedExt = item.allowedFileType?.toLowerCase() || '';
  // const mimeTypes = MIME_MAP[allowedExt] ?? [];
  // const isVideoExt = EXT_CATEGORY_MAP[allowedExt] === 'video';
  // const acceptAttr = allowedExt
  //   ? [`.${allowedExt}`, ...mimeTypes, ...(isVideoExt ? ['video/*'] : [])].join(',')
  //   : '';
  // const acceptedMimes = mimeTypes;
  const acceptAttr = Object.entries(MIME_MAP).flatMap(([ext, mimes]) => [`.${ext}`, ...mimes]).join(',');
  const allSupportedExts = Object.keys(MIME_MAP);
  const allSupportedMimes = Object.values(MIME_MAP).flat();

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const validateFile = (file) => {
    const isImageFile = getMediaCategory(file.type, file.name) === 'image';
    const MAX_FILE_SIZE = isImageFile ? 50 * 1024 * 1024 : MAX_FILE_SIZE_BYTES;
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large (${formatBytes(file.size)}). Maximum allowed size is ${formatBytes(MAX_FILE_SIZE)}.`;
    }

    // Validate that the file is one of the 21 supported types (allowedFileType no longer restricts to a single type)
    const fileExt = file.name.split('.').pop().toLowerCase();
    const isGenericBinary = !file.type || file.type === 'application/octet-stream';
    const extSupported = allSupportedExts.includes(fileExt);
    const mimeSupported = isGenericBinary || allSupportedMimes.includes(file.type);

    if (!extSupported || !mimeSupported) {
      return `Unsupported file type. Received: .${fileExt}. Please choose a different file.`;
    }

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);
    setUploadError(null);

    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      if (fileRef.current) {
        fileRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setReplacing(true);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  // Returns true once the upload has actually succeeded — callers that need
  // to act only on confirmed success (e.g. Save & Exit) should await this.
  const handleSubmit = async () => {
    if (!selectedFile) return false;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      let provenanceData = null;
      if (workflow?.captureProvenanceDetails) {
        let currentLocation = location;
        if (!currentLocation || !currentLocation.granted) {
          currentLocation = await requestLocation();
        }
        provenanceData = await captureProvenanceMetadata(currentLocation);
      }

      // Route: image file-steps → legacy; video/audio/document → multipart
      const useMultipart = shouldUseMultipartUpload(selectedFile);
      const identity = getNoteIdentity(token);

      const result = await captureSyncQueue.queuedUpload({
        token,
        assignmentItemId: item.assignmentItemId,
        file: selectedFile,
        replaceUploadContext,
        assignmentItem: item,
        provenanceData,
        useMultipart,
        offlineSupportEnabled,
        capturedByName: identity?.name,
        capturedByEmail: identity?.email,
        doUpload: async (localId) => {
          if (useMultipart) {
            // ── Multipart: browser → S3 direct ──────────────────────────
            return multipartUploadFile({
              token,
              file: selectedFile,
              assignmentItemId: item.assignmentItemId,
              assignmentItem: item,
              provenanceData,
              capturedByName: identity?.name,
              capturedByEmail: identity?.email,
              localId,
              onProgress: setUploadProgress,
            });
          }
          // ── Legacy: browser → server → S3 ───────────────────────────────
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('assignmentItemId', item.assignmentItemId);
          formData.append('assignmentItem', JSON.stringify(item));
          if (provenanceData) {
            formData.append('provenanceData', JSON.stringify(provenanceData));
          }
          if (replaceUploadContext?.replaceExisting) {
            formData.append('replaceExisting', 'true');
          }
          if (replaceUploadContext?.replaceMediaFileId) {
            formData.append('replaceMediaFileId', replaceUploadContext.replaceMediaFileId);
          }
          if (identity?.name) {
            formData.append('capturedByName', identity.name);
          }
          if (identity?.email) {
            formData.append('capturedByEmail', identity.email);
          }
          // Lets a lost-response replay dedupe server-side against this live
          // attempt instead of creating a duplicate media file. Absent when
          // offline support is off (queuedUpload calls doUpload with no
          // localId then) — nothing to dedupe against without a queue.
          if (localId) formData.append('localId', localId);

          const response = await captureClient.post(
            `/${token}/upload`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 0,
              onUploadProgress: (progressEvent) => {
                const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(pct);
              }
            }
          );
          return response.data;
        },
      });

      onCapture(selectedFile, result.identifierText || null, result);
      return true;
    } catch (error) {
      console.error('Upload failed:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        window.location.href = `/error?type=default&from=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return false;
      }
      setUploadError(parseUploadError(error));
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveAndExitClick = () => runSaveAndExit(handleSubmit, onSaveAndExit);

  useEffect(() => {
    if (window.gapi) {
      window.gapi.load("picker", () => {
        pickerReadyRef.current = true;
      });
    }
  }, []);

  // Warn user before leaving page while upload is in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = 'Upload in progress. Leaving this page will cancel the upload.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  const displayFileName = selectedFile?.name || (replacing ? null : uploadedFileName);

  return createPortal(
    <div data-theme={currentTheme} className="fixed inset-0 z-[9999] bg-base-100 overflow-y-auto capture-container">
      {/* Header */}
      <div className={`absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg landscape-cam-header ${canExpand ? 'show-all-data' : ''}`}>
        <div className="w-full max-w-full mx-auto text-left">

          {/* CaptureHeader Integration for Overlay */}
          <div className="mb-3">
            <CaptureHeader
              expiresAt={assignment?.expiresAt}
              brandLogo={workflow.branding?.logoUrl}
              branding={branding}
              className="pt-0 pb-0 px-0"
              type="overlay"
              token={cacheToken}
              onSaveAndExit={(selectedFile && !uploading && onSaveAndExit) ? handleSaveAndExitClick : undefined}
            />
          </div>

          {/* Row 2: Submitter Identity */}
          <div className={`flex flex-col mb-0 gap-1 hide-in-landscape ${canExpand && !isExpanded ? 'hide-submitter-when-collapsed' : ''}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <h1
                  title={assignment.submitterName}
                  ref={nameTooltipTriggerRef}
                  className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
                  onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
                >
                  {assignment.submitterName || 'Valued Customer'}
                </h1>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showNameTooltip}
                  onClose={() => setShowNameTooltip(false)}
                  theme={currentTheme}
                  triggerRef={nameTooltipTriggerRef}
                  type="name"
                />
              </div>
              {onOpenNotes && <NotesButton onClick={onOpenNotes} overlay />}
            </div>
            <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
              <div className="flex flex-wrap justify-between items-start w-full">
                <span className='custom-line-break'>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                <span className="shrink inline-block text-right ms-auto">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-white underline decoration-dashed focus:outline-none text-right custom-line-break"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>

          {/* Row 3: Progress Bar */}
          <div className="flex items-center gap-2 mb-4 w-full steps">
            <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
            <div className="flex-1 flex h-[5px] gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p
              ref={instructionRef}
              title={item.description}
              className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words ${!isExpanded ? 'truncate' : ''}`}
            >
              {item.description}
            </p>
            {item.required && (
              <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
                Required
              </span>
            )}
          </div>

          {/* Expand/Collapse Arrow - Only show if text is truncated */}
          {canExpand && (
            <div className="flex justify-center mt-2 -mb-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
              >
                {isExpanded ? (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* File picker area */}
      <div className="absolute inset-0 bg-base-200 flex flex-col justify-end landscape-file-picker-wrapper">
        <div className={`w-full max-w-full mx-auto bg-base-100 text-base-content px-4 pt-5 pb-[130px] flex flex-col absolute top-[200px] landscap-top-space bottom-0 left-0 right-0 z-[15] overflow-y-auto shadow-[0px_-20px_20px_rgba(0,0,0,0.25)] rounded-t-[16px] ${displayFileName ? 'file-uploaded' : ''}`}>
          {!displayFileName ? (
            <>
              <p className="text-[14px] font-semibold text-base-content mb-[10px]">Location of file</p>
              <div className="flex flex-col gap-2">
                {/* File option */}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-3 text-center w-full p-2 cursor-pointer rounded-[8px] text-base-content hover:bg-primary hover:text-white active:bg-primary active:text-white transition-all outline-none group"
                >
                  <CachedImage token={cacheToken} src={filesIconAsset} alt="Files" className={`w-8 h-8 object-contain transition-all ${isDark ? 'invert' : 'group-hover:invert group-active:invert'}`} />
                  <span className="text-sm">Files</span>
                </button>
                <input
                  ref={fileRef}
                  id="file-upload-input"
                  type="file"
                  accept={acceptAttr}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-grow gap-4 px-2">
              <div className="inline-flex items-center gap-3 bg-[#d9d9d9] rounded-[8px] px-4 py-2.5 text-black max-w-full">
                <CachedImage token={cacheToken} src={fileAttachedPin} alt="File" className="w-8 h-8 object-contain flex-shrink-0" />
                <span className="text-[20px] text-black truncate min-w-0">{displayFileName}</span>
                {/* <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-black/40 hover:text-black transition-colors p-1 flex-shrink-0"
                  title="Remove file"
                >
                  <X size={18} strokeWidth={2.5} />
                </button> */}
              </div>
            </div>
          )}

          {fileError && (
            <div className="w-full mt-2 bg-error/10 border border-error/30 rounded-xl px-3 py-2 text-xs text-error">
              {fileError}
            </div>
          )}

          {uploadError && (
            <div className="w-full mt-2 bg-error/10 border border-error/30 rounded-xl px-3 py-2 text-xs text-error">
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="fixed bottom-6 left-0 right-0 px-4 z-[20] w-full max-w-full mx-auto">
        <div className="flex items-end justify-center gap-10">
          <div className="flex flex-col items-start gap-2">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={cacheToken} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onBack}
              disabled={uploading}
              className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
            >
              Back
            </button>
          </div>
          {selectedFile ? (
            <>
              <button
                onClick={handleRemoveFile}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-md"
              >
                Replace
              </button>
              <button
                onClick={handleSubmit}
                disabled={uploading || !selectedFile || !!fileError}
                className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
              >
                {uploading ? '...' : 'Next'}
              </button>
            </>
          ) : uploadedFileName ? (
            <>
              <button
                onClick={handleRemoveFile}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-md"
              >
                Replace
              </button>
              <button
                onClick={onNext}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
              >
                Next
              </button>
            </>
          ) : (
            !item.required ? (
              <button
                onClick={onNext}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-lg"
              >
                Skip
              </button>
            ) : (
              <div className="w-[90px]" />
            )
          )}
        </div>
      </div>
      {/* Upload progress overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30">
          <div className="text-center w-3/4">
            <p className="text-white font-bold text-base mb-3">Uploading {uploadProgress}%</p>
            <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

FileUploadPane.propTypes = {
  item: PropTypes.shape({
    allowedFileType: PropTypes.string,
    referenceMediaUrl: PropTypes.string,
    description: PropTypes.string,
    required: PropTypes.bool,
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  token: PropTypes.string,
  assignment: PropTypes.object,
  workflow: PropTypes.object,
  location: PropTypes.object,
  onCapture: PropTypes.func,
  branding: PropTypes.shape({ theme: PropTypes.string }),
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  uploading: PropTypes.bool,
  setUploading: PropTypes.func,
  uploadProgress: PropTypes.number,
  setUploadProgress: PropTypes.func,
  offlineSupportEnabled: PropTypes.bool,
  onOpenNotes: PropTypes.func,
  onSaveAndExit: PropTypes.func,
};

const MediaUploader = ({ assignment, stepNumber, totalSteps, item, onCapture, onBack, onNext, captured, branding, token, retakeMode = false, isAlreadyUploaded = false, workflow, location, replaceUploadContext = null, onOpenNotes, offlineSupportEnabled = true, onSaveAndExit }) => {
  // Caching (not uploads) is skipped entirely when offline support is off — see assetCache.js.
  const cacheToken = offlineSupportEnabled ? token : null;
  const [needsUpload, setNeedsUpload] = useState(!isAlreadyUploaded);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);
  const [capturedMedia, setCapturedMedia] = useState(null);
  // capturedMedia.url can be a remote URL (previously-uploaded media being
  // reviewed) as well as a local blob: URL from a fresh capture — only the
  // image branch needs a cache fallback (video excluded, see assetCache.js).
  const { src: capturedImageSrc, tryFallback: tryCapturedImageFallback } = useCachedAssetSrc(
    cacheToken,
    capturedMedia?.type === 'video' ? null : capturedMedia?.url
  );
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [identifierText, setIdentifierText] = useState('');
  const [ocrIdentifierText, setOcrIdentifierText] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [ocrSkippedOffline, setOcrSkippedOffline] = useState(false);
  const [identifierValidationError, setIdentifierValidationError] = useState(null);
  const facingMode = 'environment';
  const [flashActive, setFlashActive] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const audioStreamRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [showReferenceMedia, setShowReferenceMedia] = useState(false);
  const referenceMediaType = item.referenceMediaUrl && getMediaCategory(null, item.referenceMediaUrl) === 'video' ? 'video' : 'image';
  const [capturing, setCapturing] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const reviewVideoRef = useRef(null);
  const displayPhotos = normalizeDisplayPhotos(captured, capturedMedia);
  const isMultiPhotoStep = item.type === 'photo' && Number(item.maxPhotos ?? 1) > 1;
  const canAddMorePhotos = isMultiPhotoStep && displayPhotos.length < Number(item.maxPhotos ?? 1);
  const [cameraLoading, setCameraLoading] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const instructionRef = useRef(null);
  const timerRef = useRef(null);

  const recordingTimeRef = useRef(0);

  const currentTheme = branding.theme || 'light';
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Keep refs in sync with state so visibilitychange handler always sees current streams
  useEffect(() => { streamRef.current = stream; }, [stream]);
  useEffect(() => { audioStreamRef.current = audioStream; }, [audioStream]);

  const capturedRef = useRef(captured);
  const retakeModeRef = useRef(retakeMode);
  useEffect(() => { capturedRef.current = captured; }, [captured]);
  useEffect(() => { retakeModeRef.current = retakeMode; }, [retakeMode]);

  // Added visibilitychange listener to cleanly shutdown camera if device goes to sleep
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Stop recorder cleanly before killing tracks
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
        // Use refs to avoid stale closure - always stops the current live tracks — always stops the current live tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => { track.enabled = false; track.stop(); });
          streamRef.current = null;
          setStream(null);
        }
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => { track.enabled = false; track.stop(); });
          audioStreamRef.current = null;
          setAudioStream(null);
        }
        // Clear the video element's srcObject so it doesn't show a black frozen frame
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      } else {
        if (!capturedRef.current || retakeModeRef.current) {
          startCameraWithRetry();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setIsExpanded(false);
    // Give browser time to layout before checking truncation
    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [item]);

  const checkTruncation = () => {
    if (instructionRef.current) {
      const wasExpanded = !instructionRef.current.classList.contains('truncate');
      if (wasExpanded) {
        instructionRef.current.classList.add('truncate');
      }

      const isTruncated = instructionRef.current.scrollWidth > instructionRef.current.clientWidth;

      if (wasExpanded) {
        instructionRef.current.classList.remove('truncate');
      }

      setCanExpand(isTruncated);
    }
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsExpanded(false);
      setTimeout(checkTruncation, 100);
    };

    window.addEventListener('resize', checkTruncation);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('resize', checkTruncation);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // beforeunload guard while upload is in progress (video recording path)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = 'Upload in progress. Leaving this page will cancel the upload.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  useEffect(() => {
    // If we have captured data and not in retake mode, set it up for display
    if (captured && !retakeMode) {
      const normalizedPhotos = Array.isArray(captured.photos) && captured.photos.length > 0
        ? captured.photos
        : (captured.previewUrl || captured.url ? [{ ...captured, previewUrl: captured.previewUrl || captured.url }] : []);
      const latestPhoto = normalizedPhotos[normalizedPhotos.length - 1] || null;
      const isRemoteUrl = latestPhoto?.previewUrl && !latestPhoto.previewUrl.startsWith('blob:');

      setCapturedMedia(prev => {
        const nextUrl = latestPhoto?.previewUrl || latestPhoto?.url || null;
        if (prev?.url === nextUrl) {
          return prev;
        }
        return latestPhoto ? { file: latestPhoto.file, url: nextUrl, type: item.type, duration: latestPhoto.duration, mimeType: latestPhoto.mimeType, fileName: latestPhoto.fileName } : null;
      });

      // FIX: Moved setMediaLoading out of setCapturedMedia callback to prevent loop bugs
      // Videos always need a loading state (even for local blob: URLs) since buffering/decoding
      // takes noticeable time on re-mount — otherwise the <video> shows a black frame until ready.
      setMediaLoading(item.type === 'video' ? !!latestPhoto : isRemoteUrl);
      // Offline-queued captures only carry identifier text nested on the photo entry
      // (top-level fields are populated later by the server-sync merge) — fall back
      // to latestPhoto so the textbox still restores before that sync happens.
      const resolvedIdentifierText = captured.customerIdentifierText || captured.identifierText || latestPhoto?.customerIdentifierText || latestPhoto?.identifierText || '';
      const resolvedOcrText = captured.ocrIdentifierText || latestPhoto?.ocrIdentifierText || '';
      setIdentifierText(resolvedIdentifierText);
      setOcrIdentifierText(resolvedOcrText);
      if (item.type === 'identifier' && latestPhoto) {
        // A customer-typed identifier with no OCR text can only mean OCR was
        // skipped (offline) at capture time — restore that flag too, or the
        // "Image not valid" gate in handleSubmit re-blocks on remount since
        // ocrSkippedOffline itself isn't part of the captured/persisted data.
        setOcrSkippedOffline(!resolvedOcrText.trim() && !!resolvedIdentifierText.trim());
        // Lock the field the same way the online "already uploaded" view does
        // (read-only box, must Retake to change) once this step already has a
        // locally-captured photo — whether or not it's synced to the server
        // yet. Otherwise a not-yet-synced offline capture stays editable on
        // every revisit, which also silently re-queues/duplicates the photo
        // when the user clicks Next again without an explicit Retake.
        setNeedsUpload(false);
      }
    } else if (captured && retakeMode) {
      setCapturedMedia(null);
      setIdentifierText('');
      setOcrIdentifierText('');
      setUploadError(null);
      setUploadProgress(0);
      setNeedsUpload(true);
      stopStream();
      stopAudioStream();
      startCamera();
    } else {
      // Start camera if no captured data
      setCapturedMedia(null);
      setIdentifierText('');
      setOcrIdentifierText('');
      setUploadError(null);
      setUploadProgress(0);
      stopStream(); // FIX: added for safety
      stopAudioStream();
      startCamera();
    }

    return () => {
      stopStream();
      stopAudioStream();
    };
  }, [item.id, captured, retakeMode]);

  const startCameraWithRetry = (facing = facingMode, attempt = 0) => {
    const delays = [800, 2000, 4000];
    if (attempt >= delays.length) {
      setCameraLoading(false);
      setCameraError('Camera could not restart after waking up. Please refresh the page.');
      return;
    }
    setTimeout(async () => {
      try {
        setCameraError(null);
        setCameraLoading(true);
        const needsAudio = item.type === 'video' && item.audioRequired;
        // Video is capped at 720p so recorded blobs stay small enough to load/decode quickly for preview;
        // photo/identifier capture keeps 1080p for image quality.
        const idealDimensions = item.type === 'video' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 };
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: idealDimensions.width }, height: { ideal: idealDimensions.height } },
          audio: needsAudio,
        });
        streamRef.current = mediaStream;
        setStream(mediaStream);
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
        if (needsAudio) { audioStreamRef.current = mediaStream; setAudioStream(mediaStream); }
        else { setAudioStream(null); }
        setCameraLoading(false);
      } catch (err) {
        if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          // Camera hardware not yet released — retry with next backoff
          startCameraWithRetry(facing, attempt + 1);
        } else {
          setCameraLoading(false);
          setCameraError(
            err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
              ? ERR_CAMERA_PERMISSION
              : 'Camera could not restart after waking up. Please refresh the page.'
          );
        }
      }
    }, delays[attempt]);
  };

  const startCamera = async (facing = facingMode) => {
    if (item.type === 'file') return;
    try {
      setCameraError(null);
      setCameraLoading(true);

      // FIX: Combined constraints to pull Audio + Video in a single hardware request
      const needsAudio = item.type === 'video' && item.audioRequired;

      // Video is capped at 720p so recorded blobs stay small enough to load/decode quickly for preview;
      // photo/identifier capture keeps 1080p for image quality.
      const idealDimensions = item.type === 'video' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 };

      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: idealDimensions.width },
          height: { ideal: idealDimensions.height }
        },
        audio: needsAudio
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn("Autoplay prevented on wake, user interaction might be needed", playError);
        }
      }

      // FIX: Set the audio state from the same stream to preserve your recording logic
      if (needsAudio) {
        audioStreamRef.current = mediaStream;
        setAudioStream(mediaStream);
      } else {
        setAudioStream(null);
      }

      setCameraLoading(false);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraLoading(false);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError(ERR_CAMERA_PERMISSION);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        startCameraWithRetry(facing, 0);
      } else {
        setCameraError(ERR_CAMERA_PERMISSION);
      }
    }
  };

  const stopStream = () => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach(track => { track.enabled = false; track.stop(); });
      streamRef.current = null;
      setStream(null);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const triggerFlash = () => {
    setFlashActive(true);
    setTimeout(() => setFlashActive(false), FLASH_DURATION_MS);
  };

  const stopAudioStream = () => {
    const a = audioStreamRef.current;
    if (a) {
      a.getTracks().forEach(track => { track.enabled = false; track.stop(); });
      audioStreamRef.current = null;
      setAudioStream(null);
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      let settled = false;

      const done = (ms) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(video.src);
        video.src = '';
        resolve(ms);
      };

      const fail = (err) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(video.src);
        video.src = '';
        reject(err);
      };

      video.onloadedmetadata = () => {
        if (video.duration && isFinite(video.duration) && !isNaN(video.duration)) {
          done(Math.round(video.duration * 1000));
        } else {
          video.currentTime = 1e101;
        }
      };

      video.ontimeupdate = () => {
        if (video.duration && isFinite(video.duration) && !isNaN(video.duration)) {
          done(Math.round(video.duration * 1000));
        }
      };

      // iOS Safari fallback: if duration never resolves after 3s, reject
      setTimeout(() => {
        console.log('duration timeout hit — falling back to recordingTimeRef');
        fail(new Error('Duration timeout'));
      }, 3000);

      video.onerror = () => fail(new Error('Video load error'));
      video.src = URL.createObjectURL(file);
    });
  };

  const capturePhoto = () => {
    setCapturing(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const dW = window.innerWidth;
    const dH = window.innerHeight;

    // Replicate object-fit:cover — find what portion of the video frame is visible
    const scale = Math.max(dW / vW, dH / vH);
    const srcX = ((vW * scale - dW) / 2) / scale;
    const srcY = ((vH * scale - dH) / 2) / scale;
    const srcW = dW / scale;
    const srcH = dH / scale;

    // Capture at native camera resolution (cropped to the on-screen aspect ratio)
    // rather than the live viewport size — window.innerWidth/innerHeight shrink
    // on iOS Safari whenever the address bar reappears (e.g. after Retake ->
    // Previous -> Capture), which was producing progressively smaller photos.
    canvas.width = srcW;
    canvas.height = srcH;

    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

    triggerFlash();

    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: MIME_IMAGE_JPEG });
      const url = URL.createObjectURL(blob);
      setCapturedMedia({ file, url, type: 'photo' });
      stopStream();
      setCapturing(false);
    }, MIME_IMAGE_JPEG, IMAGE_QUALITY);
  };


  const startRecording = () => {
    try {
      chunksRef.current = [];
      const options = [MIME_VIDEO_MP4, 'video/webm', ''];
      let mimeType = '';
      for (const type of options) {
        if (type === '' || MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Combine video and audio streams if audio is required and available
      let combinedStream = stream;
      if (item.audioRequired && audioStream) {
        combinedStream = new MediaStream([
          ...stream.getVideoTracks(),
          ...audioStream.getAudioTracks()
        ]);
      }

      // Cap bitrate so the recorded blob stays a manageable size to load/decode for preview —
      // uncapped recording at the default encoder bitrate can produce files large enough to take
      // over a minute to parse before the review <video> can play.
      const recorderOptions = { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 2_500_000 };
      const recorder = new MediaRecorder(combinedStream, recorderOptions);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) { chunksRef.current.push(e.data); }
      };

      recorder.onstop = async () => {
        setCapturing(true);
        const fallbackType = mimeType || MIME_VIDEO_MP4;
        const blob = new Blob(chunksRef.current, { type: fallbackType });
        const ext = fallbackType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `video_${Date.now()}.${ext}`, { type: fallbackType });
        const url = URL.createObjectURL(blob);

        try {
          // Get accurate video duration from the actual file
          const duration = await getVideoDuration(file);
          setCapturedMedia({
            file,
            url,
            type: 'video',
            duration: duration
          });
        } catch (error) {
          console.error('Error getting video duration:', error);
          // Fallback to recording time if duration detection fails
          setCapturedMedia({
            file,
            url,
            type: 'video',
            duration: recordingTimeRef.current * 1000
          });
        }

        stopStream();

        // Only stop audio stream if audio is not required for this item
        if (!item.audioRequired) {
          stopAudioStream();
        }
        setCapturing(false);
      };

      recorder.start(1000);
      setRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      timerRef.current = setInterval(() => {
        setRecordingTime(t => { recordingTimeRef.current = t + 1; return t + 1; });
      }, 1000);
    } catch (err) {
      console.error('Recording failed to start', err);
      alert('Recording failed to start. Please check camera permissions or try another browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const captureIdentifier = () => {
    setCapturing(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const dW = window.innerWidth;
    const dH = window.innerHeight;

    const scale = Math.max(dW / vW, dH / vH);
    const srcX = ((vW * scale - dW) / 2) / scale;
    const srcY = ((vH * scale - dH) / 2) / scale;
    const srcW = dW / scale;
    const srcH = dH / scale;

    // Capture at native camera resolution (cropped to the on-screen aspect ratio)
    // rather than the live viewport size — see capturePhoto() for why.
    canvas.width = srcW;
    canvas.height = srcH;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

    triggerFlash();

    canvas.toBlob(async (blob) => {
      const file = new File([blob], `identifier_${Date.now()}.jpg`, { type: MIME_IMAGE_JPEG });
      const url = URL.createObjectURL(blob);
      setCapturedMedia({ file, url, type: 'identifier' });
      stopStream();
      setCapturing(false);

      // Immediately run OCR on the captured image
      setOcrProcessing(true);
      setOcrError(null);
      setOcrSkippedOffline(false);

      try {
        const formData = new FormData();
        formData.append('file', file);
        if (item.identifierFormat) {
          formData.append('identifierFormat', item.identifierFormat);
        }
        formData.append('assignmentItemId', item.assignmentItemId);
        const response = await captureClient.post(
          `/${token}/ocr`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success && response.data.extractedText?.trim()) {
          const extracted = response.data.extractedText.trim();
          setOcrIdentifierText(extracted);
          setIdentifierText(extracted);
          setIdentifierValidationError(null);
          setOcrError(null);
        } else {
          setOcrIdentifierText('');
          setIdentifierText('');
          setOcrError(response.data.error || 'Image not valid. Please upload a valid image.');
        }
      } catch (err) {
        console.error('OCR failed:', err);
        setOcrIdentifierText('');
        setIdentifierText('');
        if (isNetworkError(err)) {
          // No connection to run OCR — let the customer type the identifier
          // manually instead of blocking the step entirely.
          setOcrSkippedOffline(true);
          setOcrError(null);
        } else {
          setOcrError('Image not valid. Please upload a valid image.');
        }
      } finally {
        setOcrProcessing(false);
      }
    }, MIME_IMAGE_JPEG, IMAGE_QUALITY);
  };

  // Reset the replay affordance whenever a different clip is being reviewed
  // (fresh capture, retake, or navigating to a step with different media).
  useEffect(() => {
    setVideoEnded(false);
  }, [capturedMedia?.url]);

  const handleReplayVideo = () => {
    const videoEl = reviewVideoRef.current;
    if (videoEl) {
      videoEl.currentTime = 0;
      videoEl.play();
    }
    setVideoEnded(false);
  };

  const handleRetake = () => {
    setCapturedMedia(null);
    setUploadError(null);
    setUploadProgress(0);
    setIdentifierText('');
    setOcrIdentifierText('');
    setIdentifierValidationError(null);
    setOcrError(null);
    setOcrSkippedOffline(false);
    setNeedsUpload(true); // always upload after a retake
    setRecording(false); // FIX: reset recording state so Start button shows immediately
    if (timerRef.current) clearInterval(timerRef.current);
    stopAudioStream();
    startCamera();
  };

  // Returns true once the upload has actually succeeded — callers that need
  // to act only on confirmed success (e.g. Save & Exit) should await this.
  const handleSubmit = async () => {
    if (!capturedMedia) { return false; }

    // Enforce identifier format validation before upload
    if (item.type === 'identifier') {
      if (!ocrSkippedOffline && (!ocrIdentifierText || !ocrIdentifierText.trim())) {
        setOcrError('Image not valid. Please upload a valid image.');
        return false;
      }
      if (!identifierText || !identifierText.trim()) {
        setIdentifierValidationError(`${getIdentifierLabel(item.identifierFormat) || 'Identifier'} is required`);
        return false;
      }
      const { valid, message } = validateIdentifier(item.identifierFormat, identifierText);
      if (!valid) {
        setIdentifierValidationError(message);
        return false;
      }
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Capture provenance metadata only if enabled in workflow
      let provenanceData = null;
      if (workflow?.captureProvenanceDetails) {
        let currentLocation = location;
        if (!currentLocation || !currentLocation.granted) {
          currentLocation = await requestLocation();
        }
        provenanceData = await captureProvenanceMetadata(currentLocation);
      }

      // ── Routing: video → multipart; photo/identifier → legacy ─────────────
      const isVideo = item.type === 'video';
      const identity = getNoteIdentity(token);
      const result = await captureSyncQueue.queuedUpload({
        token,
        assignmentItemId: item.assignmentItemId,
        file: capturedMedia.file,
        // Falls back to the customer-typed value when OCR never ran (offline
        // skip) — otherwise this would send an empty identifierText and the
        // backend rejects the whole upload as "Image not valid" even though
        // the image and the manually-typed identifier are both fine.
        identifierText: item.type === 'identifier' ? (ocrIdentifierText || identifierText) : null,
        customerIdentifierText: item.type === 'identifier' ? identifierText : null,
        duration: capturedMedia.duration || null,
        replaceUploadContext,
        assignmentItem: item,
        provenanceData,
        useMultipart: isVideo,
        offlineSupportEnabled,
        capturedByName: identity?.name,
        capturedByEmail: identity?.email,
        doUpload: async (localId) => {
          if (isVideo) {
            return multipartUploadFile({
              token,
              file: capturedMedia.file,
              assignmentItemId: item.assignmentItemId,
              assignmentItem: item,
              duration: capturedMedia.duration,
              provenanceData,
              capturedByName: identity?.name,
              capturedByEmail: identity?.email,
              localId,
              onProgress: setUploadProgress,
            });
          }
          // ── Legacy: photo / identifier ─────────────────────────────────
          const formData = new FormData();
          formData.append('file', capturedMedia.file);
          formData.append('assignmentItemId', item.assignmentItemId);
          formData.append('assignmentItem', JSON.stringify(item));
          if (provenanceData) {
            formData.append('provenanceData', JSON.stringify(provenanceData));
          }
          if (item.type === 'identifier') {
            formData.append('identifierText', ocrIdentifierText || identifierText);
            formData.append('customerIdentifierText', identifierText);
          }
          if (identity?.name) {
            formData.append('capturedByName', identity.name);
          }
          if (identity?.email) {
            formData.append('capturedByEmail', identity.email);
          }

          if (replaceUploadContext?.replaceExisting) {
            formData.append('replaceExisting', 'true');
          }
          if (replaceUploadContext?.replaceMediaFileId) {
            formData.append('replaceMediaFileId', replaceUploadContext.replaceMediaFileId);
          }
          if (capturedMedia.duration) {
            formData.append('duration', capturedMedia.duration);
          }
          // Lets a lost-response replay dedupe server-side against this live
          // attempt instead of creating a duplicate media file. Absent when
          // offline support is off (queuedUpload calls doUpload with no
          // localId then) — nothing to dedupe against without a queue.
          if (localId) formData.append('localId', localId);

          const response = await captureClient.post(
            `/${token}/upload`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 0,
              onUploadProgress: (progressEvent) => {
                const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(pct);
              }
            }
          );
          return response.data;
        },
      });

      if (!isVideo) {
        setNeedsUpload(false);
      }
      onCapture(
        capturedMedia.file,
        result.customerIdentifierText || identifierText || null,
        result,
      );
      return true;
    } catch (error) {
      console.error('Upload failed:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        window.location.href = `/error?type=default&from=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return false;
      }

      setUploadError(parseUploadError(error));
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Save & Exit while a fresh capture is pending — uploads it first (same
  // path "Next" would use) and only exits once that upload is confirmed to
  // have succeeded, so a failed upload never silently discards the capture.
  const handleSaveAndExitClick = () => runSaveAndExit(handleSubmit, onSaveAndExit);

  // ── File upload state (no camera) ──────────────────────────────────────────
  if (item.type === 'file') {
    return (
      <FileUploadPane
        item={item}
        token={token}
        assignment={assignment}
        workflow={workflow}
        location={location}
        onCapture={onCapture}
        branding={branding}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        onBack={onBack}
        onNext={onNext}
        uploading={uploading}
        setUploading={setUploading}
        uploadProgress={uploadProgress}
        setUploadProgress={setUploadProgress}
        captured={captured}
        canExpand={canExpand}
        instructionRef={instructionRef}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        nameTooltipTriggerRef={nameTooltipTriggerRef}
        setShowNameTooltip={setShowNameTooltip}
        setShowTooltip={setShowTooltip}
        replaceUploadContext={replaceUploadContext}
        showNameTooltip={showNameTooltip}
        showTooltip={showTooltip}
        tooltipTriggerRef={tooltipTriggerRef}
        offlineSupportEnabled={offlineSupportEnabled}
        onOpenNotes={onOpenNotes}
        onSaveAndExit={onSaveAndExit}
      />
    );
  }

  // ── Camera error state ──────────────────────────────────────────────────────
  if (cameraError) {
    const isPermissionError = cameraError.includes('access') || cameraError.includes('access');

    return (
      <div className="max-w-md mx-auto bg-base-100 rounded-2xl border border-base-200 shadow-lg p-6 text-center">

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-warning/10 rounded-xl">
            <Camera size={32} className="text-warning" strokeWidth={1.5} />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-warning font-semibold mb-1">
          Camera permission denied
        </h3>

        {/* Message */}
        <p className="text-base-content/70 text-sm leading-relaxed mb-6">
          {cameraError || "We couldn’t access your camera. Please check permissions and try again."}
        </p>

        {isPermissionError ? (
          <div className="space-y-5">

            {/* Instructions */}
            <div className="bg-base-200/60 rounded-xl p-4 text-left border border-base-300/50">
              <p className="text-xs font-medium text-base-content/60 mb-3">
                To enable camera access:
              </p>

              <ul className="space-y-2 text-sm text-base-content/80">
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">1.</span>
                  Open browser site settings
                </li>
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">2.</span>
                  Allow camera permission
                </li>
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">3.</span>
                  Refresh this page
                </li>
              </ul>
            </div>

            {/* Primary Action */}

            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary w-full h-12 rounded-xl text-sm font-semibold active:scale-[0.98]"
            >
              Refresh Page
            </button>
          </div>
        ) : (
          <button
            onClick={() => startCamera()}
            className="btn btn-primary w-full h-12 rounded-xl text-sm font-semibold active:scale-[0.98]"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // ── Review state (after capture) — Full-screen with captured photo background ──
  if (capturedMedia) {
    return createPortal(
      <div data-theme={currentTheme} className="fixed inset-0 z-[9999] bg-black overflow-hidden capture-container">

        {/* Full-bleed captured photo/video background — edge to edge */}
        {capturedMedia.type === 'video' ? (
          <video
            ref={reviewVideoRef}
            key={capturedMedia.url}
            src={capturedMedia.url}
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: mediaLoading ? 0 : 1, transition: 'opacity 0.2s' }}
            // Reveal as soon as the first frame is decodable rather than waiting for
            // canplaythrough, which can take a long time on these unindexed recordings.
            onLoadedData={() => setMediaLoading(false)}
            // If playback later stalls to buffer more data, show the loader
            // again instead of freezing silently. No longer looped (that made
            // this fire on every loop restart, flickering constantly for
            // short clips) — see handleReplayVideo for manual replay instead.
            onWaiting={() => setMediaLoading(true)}
            onPlaying={() => setMediaLoading(false)}
            onError={() => setMediaLoading(false)}
            onEnded={() => setVideoEnded(true)}
          />
        ) : (
          <img
            src={capturedImageSrc}
            alt="Captured"
            className="absolute inset-0 w-full h-full object-cover"
            onLoad={() => setMediaLoading(false)}
            onError={async () => {
              const usedFallback = await tryCapturedImageFallback();
              if (!usedFallback) setMediaLoading(false);
            }}
          />
        )}

        {/* Media Loading Overlay */}
        {mediaLoading && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
            <p className="text-white font-bold text-base">Loading...</p>
          </div>
        )}

        {/* Tap-to-replay — shown once the (no-longer-looping) video finishes.
            Wrapper spans the full screen only for centering; pointer-events
            are re-enabled just on the button so it never blocks the
            Back/Retake/Next row underneath. */}
        {capturedMedia.type === 'video' && videoEnded && !mediaLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <button
              type="button"
              onClick={handleReplayVideo}
              aria-label="Replay video"
              className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center pointer-events-auto active:scale-95 transition-transform"
            >
              <RotateCcw size={28} className="text-white" />
            </button>
          </div>
        )}

        {/* TOP DARK OVERLAY — gradient for header readability */}
        <div className="absolute top-0 left-0 right-0 h-[200px] z-[5] pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)' }}
        />

        {/* BOTTOM DARK OVERLAY — gradient for button readability */}
        <div className="absolute bottom-0 left-0 right-0 h-[120px] z-[5] pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)' }}
        />

        {/* TOP HEADER — floating over photo, using CaptureHeader with overlay styling */}
        <div className={`absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg landscape-cam-header ${canExpand ? 'show-all-data' : ''}`}>
          <div className="w-full max-w-full mx-auto text-left">

            {/* CaptureHeader Integration for Overlay */}
            <div className="mb-3">
              <CaptureHeader
                expiresAt={assignment?.expiresAt}
                brandLogo={workflow.branding?.logoUrl}
                branding={branding}
                className="pt-0 pb-0 px-0"
                type="overlay"
                token={cacheToken}
                onSaveAndExit={(!isMultiPhotoStep && !uploading && onSaveAndExit) ? (needsUpload ? handleSaveAndExitClick : onSaveAndExit) : undefined}
              />
            </div>

            {/* Row 2: Submitter Identity */}
            <div className={`flex flex-col mb-0 gap-1 hide-in-landscape ${canExpand && !isExpanded ? 'hide-submitter-when-collapsed' : ''}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h1
                    title={assignment.submitterName}
                    ref={nameTooltipTriggerRef}
                    className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
                    onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
                  >
                    {assignment.submitterName || 'Valued Customer'}
                  </h1>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showNameTooltip}
                    onClose={() => setShowNameTooltip(false)}
                    theme={currentTheme}
                    triggerRef={nameTooltipTriggerRef}
                    type="name"
                  />
                </div>
                {onOpenNotes && <NotesButton onClick={onOpenNotes} overlay />}
              </div>
              <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
                <div className="flex flex-wrap justify-between items-start w-full">
                  <span className='custom-line-break'>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                  <span className="shrink inline-block text-right ms-auto">
                    <button
                      type="button"
                      ref={tooltipTriggerRef}
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="text-white underline decoration-dashed focus:outline-none custom-line-break text-right"
                    >
                      {assignment.customerReferenceNumber}
                    </button>
                  </span>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showTooltip}
                    onClose={() => setShowTooltip(false)}
                    theme={currentTheme}
                    triggerRef={tooltipTriggerRef}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Progress Bar */}
            <div className="flex items-center gap-2 mb-4 w-full steps">
              <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
              <div className="flex-1 flex h-[5px] gap-1">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <p
                ref={instructionRef}
                title={item.description}
                className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words ${!isExpanded ? 'truncate' : ''}`}
              >
                {item.description}
              </p>
              {item.required && (
                <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
                  Required
                </span>
              )}
            </div>

            {/* Expand/Collapse Arrow - Only show if text is truncated */}
            {canExpand && (
              <div className="flex justify-center mt-2 -mb-2">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
                >
                  {isExpanded ? (
                    <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Identifier input with OCR enhancement */}
        {item.type === 'identifier' && (
          // 72px = bottom-6 (24) + Back h-[40px] + gap-2 (8) — bottom-aligns the
          // field with the global instruction "?" button sitting beside it.
          <div className={`vin-num-landscape-container absolute ${workflow?.globalInstructionUrl ? 'bottom-[72px]' : 'bottom-[90px]'} left-0 right-0 px-3 z-[15]`}>
            {/* With a global instruction configured, its "?" button sits at the
                bottom-left under this block — start the field, the disabled
                notice and the error message to its right, at the field's width
                (portrait only, see capture.css). */}
            <div className={`relative ${workflow?.globalInstructionUrl ? 'identifier-field-beside-instruction' : ''}`}>
              {(identifierValidationError || ocrError) && !ocrProcessing && (
                <div className="absolute top-auto bottom-16 text-center left-1/2 -translate-x-1/2 bg-error border border-error/20 text-white text-xs px-3 py-1 rounded-md min-w-[100%]">
                  {identifierValidationError || ocrError}
                </div>
              )}
              {(!needsUpload) && (
                <div role="alert" className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[#FFFBE4] border border-[#A65200] text-[#A65200] rounded-xl mb-3 font-bold text-[10px] shadow-sm text-center">
                  <Info size={14} className="shrink-0" />
                  <span className="text-center leading-tight">
                    This field is currently disabled and cannot be edited.
                  </span>
                </div>
              )}
              <div className="relative">
                {!needsUpload ? (
                  <div className="w-full px-4 py-3 text-base bg-gray-200 border border-gray-300 rounded-xl text-gray-600 text-center font-medium shadow-inner flex items-center justify-center cursor-not-allowed select-none relative">
                    <span className="font-mono tracking-wider">{identifierText}</span>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <Lock size={18} />
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={identifierText}
                    onChange={(e) => {
                      setIdentifierText(e.target.value);
                      if (identifierValidationError) setIdentifierValidationError(null);
                    }}
                    placeholder={ocrProcessing ? 'Detecting...' : (ocrIdentifierText ? `Edit ${getIdentifierLabel(item.identifierFormat) || 'identifier'}` : (ocrSkippedOffline ? `Enter ${getIdentifierLabel(item.identifierFormat) || 'identifier'} manually` : 'Capture image to detect identifier'))}
                    disabled={uploading || ocrProcessing || (!ocrIdentifierText && !ocrSkippedOffline)}
                    maxLength={50}
                    className={`w-full px-4 py-3 text-base bg-white/80 backdrop-blur-lg border rounded-xl text-gray-900 text-center outline-none disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100 disabled:border-gray-300 disabled:cursor-not-allowed focus:ring-primary placeholder-gray-400 transition-all ${identifierValidationError ? 'border-red-400 ring-1 ring-red-400/60' : 'border-white/30'
                      }`}
                  />
                )}
              </div>

            </div>
          </div>
        )}

        {/* OCR Processing overlay — full page loader */}
        {item.type === 'identifier' && ocrProcessing && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-[20]">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
            <p className="text-white font-bold text-base">Processing Image...</p>
          </div>
        )}

        {/* Upload error — floating over photo */}
        {uploadError && (
          <div className="absolute inset-0 flex items-center justify-center px-4">

            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 " />

            {/* Error Card */}
            <div className="relative w-full max-w-xs bg-base-100 rounded-2xl shadow-xl border border-error/30 p-5 text-center">

              {/* Icon */}
              <div className="flex justify-center mb-3">
                <div className="bg-error/10 p-3 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-error"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.1 0 1.8-1.2 1.26-2.1L13.26 4.1c-.55-.9-1.97-.9-2.52 0L3.67 16.9c-.54.9.16 2.1 1.26 2.1z" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-error font-semibold text-sm mb-1">
                Upload Failed
              </h3>

              {/* Message */}
              <p className="text-base-content/70 text-sm leading-relaxed mb-4">
                {uploadError || "Something went wrong while uploading. Please try again."}
              </p>

            </div>
          </div>
        )}

        {/* BOTTOM BUTTONS — 3 buttons floating over photo */}
        <div className="absolute w-full max-w-full mx-auto bottom-6 left-0 right-0 px-3 flex items-end justify-between md:justify-center lg:justify-center gap-10 z-10 landscape-shutter-row camera-icon-click">
          {/* Back Button */}
          <div className="flex flex-col items-start gap-2">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={cacheToken} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onBack}
              disabled={uploading}
              className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
            >
              Back
            </button>
          </div>

          {/* Retake Button */}
          <button
            onClick={handleRetake}
            disabled={uploading}
            className="w-[100px] h-[40px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
          >
            Retake
          </button>

          {/* Next Button */}
          <button
            onClick={needsUpload ? handleSubmit : onNext}
            disabled={uploading}
            className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
          >
            {uploading ? '...' : (isMultiPhotoStep ? (canAddMorePhotos ? 'Done' : 'Continue') : 'Next')}
          </button>
        </div>

        {/* Upload progress overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30">
            <div className="text-center w-3/4">
              <p className="text-white font-bold text-base mb-3">Uploading {uploadProgress}%</p>
              <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          </div>
        )}

      </div >,
      document.body
    );
  }

  // ── Full-screen live camera ─────────────────────────────────────────────────

  return createPortal(
    <div data-theme={currentTheme} className="capture-container" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', overflow: 'hidden' }}>

      {/* Flash effect */}
      {flashActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'white', opacity: 0.85, pointerEvents: 'none' }} />
      )}

      {/* Live video — full screen */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          ...(facingMode === 'user' ? { transform: 'scaleX(-1)' } : {})
        }}
        onLoadedMetadata={() => setCameraLoading(false)}
        onError={() => setCameraLoading(false)}
      />

      {/* Top Header Overlay Design */}
      <div className={`absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg landscape-cam-header ${canExpand ? 'show-all-data' : ''}`}>
        <div className="w-full max-w-full mx-auto text-left">

          {/* CaptureHeader Integration for Overlay — no Save & Exit here, nothing captured yet */}
          <div className="mb-3">
            <CaptureHeader
              expiresAt={assignment?.expiresAt}
              brandLogo={workflow.branding?.logoUrl}
              branding={branding}
              className="pt-0 pb-0 px-0"
              type="overlay"
              token={cacheToken}
            />
          </div>

          {/* Row 2: Submitter Identity */}
          <div className={`flex flex-col mb-0 gap-1 hide-in-landscape ${canExpand && !isExpanded ? 'hide-submitter-when-collapsed' : ''}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <h1
                  title={assignment.submitterName}
                  ref={nameTooltipTriggerRef}
                  className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
                  onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
                >
                  {assignment.submitterName || 'Valued Customer'}
                </h1>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showNameTooltip}
                  onClose={() => setShowNameTooltip(false)}
                  theme={currentTheme}
                  triggerRef={nameTooltipTriggerRef}
                  type="name"
                />
              </div>
              {onOpenNotes && <NotesButton onClick={onOpenNotes} overlay />}
            </div>
            <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
              <div className="flex flex-wrap justify-between items-start w-full">
                <span className='custom-line-break'>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                <span className="shrink inline-block text-right">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-white underline decoration-dashed focus:outline-none custom-line-break text-right"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>

          {/* Row 3: Progress Bar */}
          <div className="flex items-center gap-2 mb-4 w-full steps">
            <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
            <div className="flex-1 flex h-[5px] gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0 max-w-[75%]">
              {item.referenceMediaUrl && (
                <button
                  type="button"
                  onClick={() => setShowReferenceMedia(true)}
                  title="View reference media"
                  aria-label="View step instructions"
                  className="w-6 h-6 rounded-full bg-primary shadow-sm border border-primary flex items-center justify-center shrink-0"
                >
                  <HelpCircle size={14} className="text-white" />
                </button>
              )}
              <p
                ref={instructionRef}
                title={item.description}
                className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug break-words min-w-0 flex-1 ${!isExpanded ? 'truncate' : ''}`}
              >
                {item.description}
              </p>
            </div>
            {item.required && (
              <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm shrink-0">
                Required
              </span>
            )}
          </div>

          {/* Expand/Collapse Arrow - Only show if text is truncated */}
          {canExpand && (
            <div className="flex justify-center mt-2 -mb-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
              >
                {isExpanded ? (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Capture Loading Overlay */}
      {capturing && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white font-bold text-base">Capturing...</p>
        </div>
      )}

      {/* Camera Loading Overlay */}
      {cameraLoading && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white font-bold text-base">Starting Camera...</p>
        </div>
      )}

      {/* Shutter Button with Back/Skip for optional steps */}
      <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 20, paddingLeft: '16px', paddingRight: '16px' }} className="landscape-shutter-row camera-icon-click">
        <div className="w-[90px] flex justify-end">
          <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={cacheToken} className="!w-11 !h-11 shrink-0" />
        </div>
        <div className="landscape-shutter-spacer" />

        {/* Capture Button - Portrait View */}
        <div style={{ position: 'relative' }} className='ms-auto me-auto camera-click landscape-camera-portrait'>
          {/* Crisp White Outer Ring */}
          <div className="absolute -inset-[5px] rounded-full border-[3px] border-white shadow-lg pointer-events-none camera--icon" />

          {item.type === 'photo' && (
            <button
              onClick={capturePhoto}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50"
            >
              <CachedImage token={cacheToken} src={cameraWhiteIcon} alt="Capture" className="w-[42px] object-contain pointer-events-none m-auto" />
            </button>
          )}

          {item.type === 'video' && (
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={capturing}
              className={`w-[74px] h-[74px] rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50 ${recording ? 'bg-red-500' : 'bg-primary'}`}
            >
              {recording ? <div className="w-8 h-8 bg-white rounded-sm" /> : <CachedImage token={cacheToken} src={videoWhiteIcon} alt="Record" className="w-[42px] object-contain pointer-events-none" />}
            </button>
          )}

          {item.type === 'identifier' && (
            <button
              onClick={captureIdentifier}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all text-white relative z-10 outline-none disabled:opacity-50"
            >
              <CachedImage token={cacheToken} src={scannerWhiteIcon} alt="scanner-white" className="w-[42px] object-contain pointer-events-none" />
            </button>
          )}
        </div>

        {/* Capture Button - Landscape View (Right Side) */}
        <div style={{ position: 'relative' }} className='landscape-camera-landscape'>
          {/* Crisp White Outer Ring */}
          <div className="absolute -inset-[5px] rounded-full border-[3px] border-white shadow-lg pointer-events-none" />

          {item.type === 'photo' && (
            <button
              onClick={capturePhoto}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50"
            >
              <CachedImage token={cacheToken} src={cameraWhiteIcon} alt="Capture" className="w-[42px] object-contain pointer-events-none" />
            </button>
          )}

          {item.type === 'video' && (
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={capturing}
              className={`w-[74px] h-[74px] rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50 ${recording ? 'bg-red-500' : 'bg-primary'}`}
            >
              {recording ? <div className="w-8 h-8 bg-white rounded-sm" /> : <CachedImage token={cacheToken} src={videoWhiteIcon} alt="Record" className="w-[42px] object-contain pointer-events-none" />}
            </button>
          )}

          {item.type === 'identifier' && (
            <button
              onClick={captureIdentifier}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all text-white relative z-10 outline-none disabled:opacity-50"
            >
              <CachedImage token={cacheToken} src={scannerWhiteIcon} alt="scanner-white" className="w-[42px] object-contain pointer-events-none" />
            </button>
          )}
        </div>

        {/* Skip Button - Only show for optional steps */}
        {!item.required ? (
          <button
            onClick={onNext}
            className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content backdrop-blur-sm font-bold border-none cursor-pointer transition-transform shadow-lg"
          >
            Skip
          </button>
        ) : (
          // Placeholder to maintain layout symmetry for required steps
          <div className="landscape-shutter-spacer w-[90px]" />
        )}
      </div>

      {showReferenceMedia && (
        <FullScreenViewer
          url={item.referenceMediaUrl}
          type={referenceMediaType}
          onClose={() => setShowReferenceMedia(false)}
          token={cacheToken}
        />
      )}
    </div>,
    document.body
  );
};

MediaUploader.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    audioRequired: PropTypes.bool,
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    required: PropTypes.bool,
    identifierFormat: PropTypes.string,
    allowedFileType: PropTypes.string,
    referenceMediaUrl: PropTypes.string,
  }),
  onCapture: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  captured: PropTypes.shape({
    file: PropTypes.object,
    previewUrl: PropTypes.string,
    identifierText: PropTypes.string,
    customerIdentifierText: PropTypes.string,
    ocrIdentifierText: PropTypes.string,
  }),
  branding: PropTypes.shape({
    theme: PropTypes.string,
    primaryColor: PropTypes.string,
  }),
  token: PropTypes.string,
  retakeMode: PropTypes.bool,
  isAlreadyUploaded: PropTypes.bool,
  replaceUploadContext: PropTypes.shape({
    replaceExisting: PropTypes.bool,
    replaceMediaFileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  workflow: PropTypes.shape({
    captureProvenanceDetails: PropTypes.bool,
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  location: PropTypes.object,
  offlineSupportEnabled: PropTypes.bool,
  onOpenNotes: PropTypes.func,
  onSaveAndExit: PropTypes.func,
};

export default MediaUploader;
